import {useCallback, useState} from 'react'
import {Image, Platform, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {launchImageLibraryAsync} from 'expo-image-picker'

import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import {Text} from '#/components/Typography'
import {useStudioCreateVideo} from '#/state/queries/studio'
import {useSession} from '#/state/session'
import {
  getStudioAuthHeaders,
  type StudioVideoFormat,
  type StudioVideoStatus,
} from '#/lib/api/studio'
import type {DialogControlProps} from '#/components/Dialog'
import * as Toast from '#/view/com/util/Toast'

type ThumbnailInput =
  | {type: 'file'; file: File}
  | {type: 'uri'; uri: string; fileSize?: number; mimeType?: string}
  | null

const AWS_S3_BUCKET =
  (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_AWS_S3_BUCKET) ||
  'shxrevappblob'
const AWS_S3_REGION =
  (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_AWS_S3_REGION) ||
  'us-east-2'
const DEFAULT_PRESIGNED_ENDPOINT =
  'https://shxre.social/api/s3/presigned-url'
const PRESIGNED_URL_ENDPOINT =
  (typeof process !== 'undefined' &&
    process.env?.EXPO_PUBLIC_S3_PRESIGNED_URL_ENDPOINT) ||
  DEFAULT_PRESIGNED_ENDPOINT
// Ensure we never use an empty or broken endpoint (e.g. wrong env in build).
// Common typo: share.social -> shxre.social
function getPresignedEndpoint(): string {
  if (!PRESIGNED_URL_ENDPOINT || !PRESIGNED_URL_ENDPOINT.includes('presigned-url'))
    return DEFAULT_PRESIGNED_ENDPOINT
  try {
    const u = new URL(PRESIGNED_URL_ENDPOINT)
    if (u.hostname === 'share.social') return DEFAULT_PRESIGNED_ENDPOINT
  } catch {
    return DEFAULT_PRESIGNED_ENDPOINT
  }
  return PRESIGNED_URL_ENDPOINT
}
const presignedEndpoint = getPresignedEndpoint()

function getMakePublicEndpoint(): string {
  return presignedEndpoint.replace('/api/s3/presigned-url', '/api/s3/make-public')
}

async function makeS3ObjectPublic(key: string): Promise<void> {
  const res = await fetch(getMakePublicEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getStudioAuthHeaders(),
    },
    body: JSON.stringify({key}),
  })
  if (!res.ok) {
    console.warn('Failed to set S3 object public:', key, res.status)
  }
}

export function StudioUploadDialog({
  control,
  onSuccess,
}: {
  control: DialogControlProps
  onSuccess?: () => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const {currentAccount} = useSession()
  const createVideo = useStudioCreateVideo()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<StudioVideoStatus>('draft')
  const [videoFormat, setVideoFormat] = useState<StudioVideoFormat>('standard')
  const [videoPath, setVideoPath] = useState<string | null>(null)
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [thumbnail, setThumbnail] = useState<ThumbnailInput>(null)
  const [error, setError] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)

  const reset = useCallback(() => {
    setTitle('')
    setDescription('')
    setStatus('draft')
    setVideoFormat('standard')
    setVideoPath(null)
    setVideoFile(null)
    setThumbnail(null)
    setError(null)
    setUploadProgress(0)
    setIsUploading(false)
  }, [])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      setVideoFile(file)
      setVideoPath(null)
      setError(null)
    },
    [],
  )

  const handleThumbnailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      setThumbnail(file ? {type: 'file', file} : null)
      setError(null)
    },
    [],
  )

  const handlePickThumbnailNative = useCallback(async () => {
    try {
      const res = await launchImageLibraryAsync({
        mediaTypes: ['images'],
        quality: 0.8,
        selectionLimit: 1,
      })
      const asset = res.assets?.[0]
      if (!asset?.uri) return
      setThumbnail({
        type: 'uri',
        uri: asset.uri,
        fileSize: asset.fileSize,
        mimeType: asset.mimeType ?? 'image/jpeg',
      })
      setError(null)
    } catch {
      // user cancelled or error
    }
  }, [])

  const uploadToS3 = useCallback(async (): Promise<string | null> => {
    if (!videoFile) return null
    const key = `studio/${Date.now()}-${videoFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
    const contentType = videoFile.type || 'video/mp4'
    const res = await fetch(presignedEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getStudioAuthHeaders(),
      },
      body: JSON.stringify({
        key,
        contentType,
        fileSize: videoFile.size,
      }),
    })
    if (!res.ok) {
      if (res.status === 404) {
        throw new Error(
          _(msg`Upload server not found (404). The backend at ${presignedEndpoint} may not be running or may need to be redeployed with the presigned URL route.`),
        )
      }
      const err = await res.json().catch(() => ({}))
      throw new Error((err as {error?: string}).error || res.statusText)
    }
    const {url} = (await res.json()) as {url: string}
    const xhr = new XMLHttpRequest()
    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', e => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 100))
        }
      })
      xhr.addEventListener('load', async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadProgress(100)
          await makeS3ObjectPublic(key)
          const s3Url = `https://${AWS_S3_BUCKET}.s3.${AWS_S3_REGION}.amazonaws.com/${key}`
          resolve(s3Url)
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`))
        }
      })
      xhr.addEventListener('error', () => reject(new Error('Upload failed')))
      xhr.open('PUT', url)
      xhr.setRequestHeader('Content-Type', contentType)
      xhr.send(videoFile)
    })
  }, [videoFile])

  const uploadThumbnailToS3 = useCallback(
    async (input: ThumbnailInput): Promise<string> => {
      if (!input) throw new Error('No thumbnail')
      const baseName =
        input.type === 'file'
          ? input.file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
          : 'thumb.jpg'
      const key = `studio/thumbnails/${Date.now()}-${baseName}`
      const contentType =
        input.type === 'file'
          ? input.file.type || 'image/jpeg'
          : input.mimeType || 'image/jpeg'
      let fileSize: number
      let body: Blob | File
      if (input.type === 'file') {
        fileSize = input.file.size
        body = input.file
      } else {
        const blob = await (await fetch(input.uri)).blob()
        fileSize = input.fileSize ?? blob.size
        body = blob
      }
      const res = await fetch(presignedEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getStudioAuthHeaders(),
        },
        body: JSON.stringify({
          key,
          contentType,
          fileSize,
        }),
      })
      if (!res.ok) {
        if (res.status === 404) {
          throw new Error(
            _(msg`Upload server not found (404). The backend may need to be redeployed.`),
          )
        }
        const err = await res.json().catch(() => ({}))
        throw new Error((err as {error?: string}).error || res.statusText)
      }
      const {url} = (await res.json()) as {url: string}
      await fetch(url, {
        method: 'PUT',
        headers: {'Content-Type': contentType},
        body,
      })
      await makeS3ObjectPublic(key)
      return `https://${AWS_S3_BUCKET}.s3.${AWS_S3_REGION}.amazonaws.com/${key}`
    },
    [_],
  )

  const handleSubmit = useCallback(async () => {
    setError(null)
    if (!currentAccount?.did) {
      setError(_(msg`You must be logged in so this video appears in My Videos.`))
      return
    }
    if (!title.trim()) {
      setError(_(msg`Title is required`))
      return
    }
    let path = videoPath
    if (videoFile && !path) {
      try {
        setIsUploading(true)
        setUploadProgress(0)
        path = (await uploadToS3()) ?? null
        if (!path) {
          setError(_(msg`Failed to upload video`))
          return
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : _(msg`Upload failed`))
        return
      } finally {
        setIsUploading(false)
      }
    }
    if (!path) {
      setError(_(msg`Please select a video file`))
      return
    }
    let thumbnailPath: string | undefined
    if (thumbnail) {
      try {
        thumbnailPath = await uploadThumbnailToS3(thumbnail)
      } catch (e) {
        setError(
          e instanceof Error ? e.message : _(msg`Failed to upload thumbnail`),
        )
        return
      }
    }
    const ownerDid = currentAccount?.did
    if (!ownerDid) {
      setError(_(msg`You must be logged in so this video appears in My Videos.`))
      return
    }
    try {
      await createVideo.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        video_path: path,
        thumbnail_path: thumbnailPath,
        status,
        owner_did: ownerDid,
        video_format: videoFormat,
      })
      control.close(() => {
        reset()
        Toast.show(_(msg`Video uploaded!`), 'check')
        onSuccess?.()
      })
    } catch (e) {
      setError(
        e instanceof Error ? e.message : _(msg`Failed to create video record`),
      )
    }
  }, [
    title,
    description,
    status,
    videoPath,
    videoFile,
    uploadToS3,
    uploadThumbnailToS3,
    thumbnail,
    createVideo,
    control,
    reset,
    onSuccess,
    _,
    currentAccount,
    videoFormat,
  ])

  const handleClose = useCallback(() => {
    control.close(() => reset())
  }, [control, reset])

  return (
    <Dialog.Outer control={control} onClose={handleClose}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={_(msg`Upload video`)}>
        <Dialog.Header>
          <Dialog.HeaderText>
            <Trans>Upload video</Trans>
          </Dialog.HeaderText>
        </Dialog.Header>

        <TextField.LabelText>
          <Trans>Title</Trans>
        </TextField.LabelText>
        <TextField.Root>
          <TextField.Input
            label={_(msg`Title`)}
            placeholder={_(msg`Enter title`)}
            value={title}
            onChangeText={setTitle}
          />
        </TextField.Root>

        <TextField.LabelText>
          <Trans>Description</Trans>
        </TextField.LabelText>
        <TextField.Root>
          <TextField.Input
            label={_(msg`Description`)}
            placeholder={_(msg`Optional description`)}
            value={description}
            onChangeText={setDescription}
            multiline
          />
        </TextField.Root>

        <View style={[a.pt_md]}>
          <Text style={[a.text_sm, a.font_medium, a.pb_xs, t.atoms.text]}>
            <Trans>Video size</Trans>
          </Text>
          <Text style={[a.text_xs, a.pb_sm, t.atoms.text_contrast_medium]}>
            <Trans>Standard = horizontal (SHXRE Videos). Clips = vertical (Clips feed).</Trans>
          </Text>
          <View style={[a.flex_row, a.gap_sm]}>
            <Button
              label={_(msg`SHXRE Videos (horizontal)`)}
              size="small"
              color={videoFormat === 'standard' ? 'primary' : 'secondary'}
              variant={videoFormat === 'standard' ? 'solid' : 'outline'}
              onPress={() => setVideoFormat('standard')}>
              <ButtonText>
                <Trans>SHXRE Videos</Trans>
              </ButtonText>
            </Button>
            <Button
              label={_(msg`Clips (vertical)`)}
              size="small"
              color={videoFormat === 'short' ? 'primary' : 'secondary'}
              variant={videoFormat === 'short' ? 'solid' : 'outline'}
              onPress={() => setVideoFormat('short')}>
              <ButtonText>
                <Trans>Clips</Trans>
              </ButtonText>
            </Button>
          </View>
        </View>

        <View style={[a.pt_md]}>
          <Text style={[a.text_sm, a.font_medium, a.pb_xs, t.atoms.text]}>
            <Trans>Video file</Trans>
          </Text>
          {Platform.OS === 'web' ? (
            <>
              <input
                type="file"
                accept="video/*"
                onChange={handleFileChange as (e: any) => void}
                style={{display: 'block', marginTop: 4}}
              />
              {videoFile && (
                <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
                  {videoFile.name} ({(videoFile.size / 1024 / 1024).toFixed(1)} MB)
                </Text>
              )}
              {uploadProgress > 0 && (
                <View style={[a.pt_sm]}>
                  <View
                    style={[
                      a.overflow_hidden,
                      a.rounded_sm,
                      t.atoms.bg_contrast_100,
                      {height: 8},
                    ]}>
                    <View
                      style={[
                        a.rounded_sm,
                        {height: 8, width: `${uploadProgress}%`},
                        {backgroundColor: t.palette.primary_500},
                      ]}
                    />
                  </View>
                  <Text
                    style={[
                      a.text_xs,
                      a.pt_xs,
                      t.atoms.text_contrast_medium,
                    ]}>
                    {uploadProgress < 100
                      ? _(msg`Uploading… ${uploadProgress}%`)
                      : _(msg`Upload complete`)}
                  </Text>
                </View>
              )}
            </>
          ) : (
            <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
              <Trans>Video upload is available on web.</Trans>
            </Text>
          )}
        </View>

        <View style={[a.pt_md]}>
          <Text style={[a.text_sm, a.font_medium, a.pb_xs, t.atoms.text]}>
            <Trans>Thumbnail (optional)</Trans>
          </Text>
          {Platform.OS === 'web' ? (
            <>
              <input
                type="file"
                accept="image/*"
                onChange={handleThumbnailChange as (e: any) => void}
                style={{display: 'block', marginTop: 4}}
              />
              {thumbnail && thumbnail.type === 'file' && (
                <View style={[a.pt_xs, a.flex_row, a.gap_sm, a.align_center]}>
                  {thumbnail.file.type.startsWith('image/') && (
                    <img
                      src={URL.createObjectURL(thumbnail.file)}
                      alt=""
                      style={{
                        width: 80,
                        height: 45,
                        objectFit: 'cover',
                        borderRadius: 4,
                      }}
                    />
                  )}
                  <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
                    {thumbnail.file.name}
                  </Text>
                  <Button
                    label={_(msg`Remove thumbnail`)}
                    size="tiny"
                    color="secondary"
                    variant="outline"
                    onPress={() => setThumbnail(null)}>
                    <ButtonText>
                      <Trans>Remove</Trans>
                    </ButtonText>
                  </Button>
                </View>
              )}
            </>
          ) : (
            <>
              <Button
                label={_(msg`Choose thumbnail`)}
                size="small"
                color="secondary"
                variant="outline"
                onPress={handlePickThumbnailNative}>
                <ButtonText>
                  <Trans>Choose thumbnail</Trans>
                </ButtonText>
              </Button>
              {thumbnail && thumbnail.type === 'uri' && (
                <View style={[a.pt_xs, a.flex_row, a.gap_sm, a.align_center]}>
                  <Image
                    source={{uri: thumbnail.uri}}
                    style={{width: 80, height: 45, borderRadius: 4}}
                    resizeMode="cover"
                  />
                  <Button
                    label={_(msg`Remove thumbnail`)}
                    size="tiny"
                    color="secondary"
                    variant="outline"
                    onPress={() => setThumbnail(null)}>
                    <ButtonText>
                      <Trans>Remove</Trans>
                    </ButtonText>
                  </Button>
                </View>
              )}
            </>
          )}
        </View>

        <View style={[a.pt_md]}>
          <Text style={[a.text_sm, a.font_medium, a.pb_xs, t.atoms.text]}>
            <Trans>Status</Trans>
          </Text>
          <View style={[a.flex_row, a.gap_md]}>
            <Button
              label={_(msg`Draft`)}
              size="small"
              color={status === 'draft' ? 'primary' : 'secondary'}
              variant={status === 'draft' ? 'solid' : 'outline'}
              onPress={() => setStatus('draft')}>
              <ButtonText>
                <Trans>Draft</Trans>
              </ButtonText>
            </Button>
            <Button
              label={_(msg`Published`)}
              size="small"
              color={status === 'published' ? 'primary' : 'secondary'}
              variant={status === 'published' ? 'solid' : 'outline'}
              onPress={() => setStatus('published')}>
              <ButtonText>
                <Trans>Published</Trans>
              </ButtonText>
            </Button>
          </View>
        </View>

        {error ? (
          <Text
            style={[a.text_sm, a.pt_md, {color: t.palette.negative_400}]}>
            {error}
          </Text>
        ) : null}

        <View style={[a.flex_row, a.gap_md, a.pt_xl]}>
          <Button
            label={_(msg`Cancel`)}
            size="large"
            color="secondary"
            onPress={handleClose}>
            <ButtonText>
              <Trans>Cancel</Trans>
            </ButtonText>
          </Button>
          <Button
            label={_(msg`Upload`)}
            size="large"
            color="primary"
            onPress={handleSubmit}
            disabled={!currentAccount?.did || createVideo.isPending || isUploading}>
            <ButtonText>
              <Trans>Upload</Trans>
            </ButtonText>
          </Button>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}
